import { useEffect } from "react";
import { Spade, Heart, Users, Shield, Trophy, Clock } from "lucide-react";

export default function About() {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.pexels.com/photos/6532369/pexels-photo-6532369.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-casino-black" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-gold uppercase tracking-[0.3em] text-sm mb-6 font-accent">
            Veteran Owned & Operated
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            About <span className="gold-gradient">SMSC</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            The premier poker room in San Marcos, Texas
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-casino-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-gold uppercase tracking-widest text-sm mb-4">Our Mission</p>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
                A Welcoming Space for All Players
              </h2>
              <div className="space-y-4 text-white/70">
                <p>
                  San Marcos Social Club is the premier poker room in San Marcos, dedicated to providing 
                  a welcoming, fun, and safe environment for all poker players. Whether you're learning 
                  how to play poker, exploring the world of poker, or you're an experienced pro ready 
                  to go all in with Texas Holdem, we have something for everyone.
                </p>
                <p>
                  Our mission is to foster a strong sense of community within our gaming community 
                  through engaging games and exciting events. We believe poker is more than just a 
                  game—it's a way to connect, compete, and create lasting memories.
                </p>
                <p>
                  As a veteran-owned and operated establishment, we bring discipline, integrity, 
                  and a commitment to excellence to everything we do.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-gold/20">
                <img 
                  src="https://images.pexels.com/photos/2555292/pexels-photo-2555292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Poker action"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gold/10 rounded-2xl flex items-center justify-center">
                <Spade className="w-16 h-16 text-gold" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-casino-dark">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-gold uppercase tracking-widest text-sm mb-4">What We Stand For</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white">Our Values</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: "Community First",
                description: "We foster a strong sense of community among poker enthusiasts of all levels."
              },
              {
                icon: Shield,
                title: "Safe Environment",
                description: "Your safety and comfort are our top priorities. We maintain a secure, welcoming space."
              },
              {
                icon: Heart,
                title: "Beginner Friendly",
                description: "New to poker? We welcome beginners and offer a supportive learning environment."
              },
              {
                icon: Trophy,
                title: "Competitive Play",
                description: "For experienced players, we offer challenging tournaments and competitive cash games."
              },
              {
                icon: Clock,
                title: "Flexible Hours",
                description: "Flexible hours to accommodate various schedules for your convenience."
              },
              {
                icon: Spade,
                title: "Veteran Owned",
                description: "Proudly veteran-owned and operated with a commitment to excellence and integrity."
              },
            ].map((value) => (
              <div 
                key={value.title}
                className="glass-card p-8"
                data-testid={`value-card-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-gold" />
                </div>
                <h3 className="font-heading text-xl text-white mb-3">{value.title}</h3>
                <p className="text-white/60">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 bg-casino-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="glass-card p-8 md:p-12 lg:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-gold uppercase tracking-widest text-sm mb-4">The Experience</p>
                <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
                  More Than Just Poker
                </h2>
                <div className="space-y-4 text-white/70">
                  <p>
                    At San Marcos Social Club, we've created an atmosphere that combines the excitement 
                    of Las Vegas-style poker with the warmth of a local community establishment.
                  </p>
                  <p>
                    Enjoy beverages and snacks while you play. Take advantage of our skill and 
                    sweepstakes machines. Join our weekly tournaments or hop into a cash game 
                    whenever the mood strikes.
                  </p>
                  <p>
                    Our experienced staff ensures smooth gameplay and fair dealing, while our 
                    comfortable setting lets you focus on what matters most—the cards.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square rounded-xl bg-gradient-to-br from-gold/20 to-transparent p-6 flex flex-col items-center justify-center text-center">
                  <span className="font-heading text-5xl text-gold mb-2">7</span>
                  <span className="text-white/60 text-sm uppercase tracking-wider">Days a Week</span>
                </div>
                <div className="aspect-square rounded-xl bg-gradient-to-br from-casino-red/20 to-transparent p-6 flex flex-col items-center justify-center text-center">
                  <span className="font-heading text-5xl text-white mb-2">13</span>
                  <span className="text-white/60 text-sm uppercase tracking-wider">Hours Daily</span>
                </div>
                <div className="aspect-square rounded-xl bg-gradient-to-br from-casino-red/20 to-transparent p-6 flex flex-col items-center justify-center text-center">
                  <span className="font-heading text-5xl text-white mb-2">6+</span>
                  <span className="text-white/60 text-sm uppercase tracking-wider">Weekly Events</span>
                </div>
                <div className="aspect-square rounded-xl bg-gradient-to-br from-gold/20 to-transparent p-6 flex flex-col items-center justify-center text-center">
                  <span className="font-heading text-5xl text-gold mb-2">$1K</span>
                  <span className="text-white/60 text-sm uppercase tracking-wider">Guarantees</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
